import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import * as dayjs from 'dayjs';
import { Message } from '../../generic/classes/message';
import { ApplicationService } from '../../generic/service/application.service';
import { FilterService } from '../../generic/service/filter.service';
import { DatepickerComponent } from '../../datepicker/datepicker.component';

interface RangeFilter {
    range: number[];
    filter: string;
}

@Component({
    selector: 'filter-order-history',
    styleUrls: ['./filter.component.scss', './featuregroup.component.scss'],
    templateUrl: './filter.order.history.html',
    standalone: true,
    imports: [DatepickerComponent],
})
export class FilterOrderHistoryComponent implements OnInit, OnDestroy {
    // Work around i18n limitations
    // @see https://github.com/angular/angular/issues/11405#issuecomment-280850945
    @Input()
    public message: string;

    dateFrom: Date | null;
    dateTo: Date | null;
    today: Date | null;

    lastTwoWeeksRange: number[] = [];
    lastMonthRange: number[] = [];
    samePeriodLastYearRange: number[] = [];

    lastTwoWeeksChecked = false;
    lastMonthChecked = false;
    samePeriodLastYearChecked = false;

    constructor(
        private filterService: FilterService,
        private applicationService: ApplicationService,
    ) {}

    private static getDateForTwoWeeksAgo(): Date {
        return dayjs().subtract(2, 'week').startOf('day').toDate();
    }

    ngOnInit(): void {
        this.initializeMessage();
        this.initializeDates();
        this.setPeriod(false);

        // Set default selection
        this.setLastTwoWeeks();
    }

    ngOnDestroy(): void {
        this.dateFrom = null;
        this.dateTo = null;
        this.today = null;
        this.applicationService.message = null;
    }

    /**
     * Set the from date input field by selecting a date from a 'calendar'
     * When choosing a date from the 'calendar' dropdown a 'Date' instance will be
     * set.
     *
     * @param {Date} value
     */
    setDateFrom(value: Date): void {
        this.dateFrom = value;
        this.setAndCheckPeriod();
    }

    /**
     * Set the to date input field by selecting a date from a 'calendar'
     * When choosing a date from the 'calendar' dropdown a 'Date' instance will be
     * set.
     *
     * @param {Date} value
     */
    setDateTo(value: Date): void {
        this.dateTo = value;
        this.setAndCheckPeriod();
    }

    setAndCheckPeriod() {
        this.setPeriod(true);

        this.normalizeDate(this.dateFrom);
        this.normalizeDate(this.dateTo);

        this.checkSelectedRange();
    }

    setLastTwoWeeks(): void {
        this.setDateTo(this.today as Date);
        this.setDateFrom(FilterOrderHistoryComponent.getDateForTwoWeeksAgo());

        this.lastTwoWeeksRange = [
            this.dateFrom?.getTime() || 0,
            this.today?.getTime() || 0,
        ];
        this.lastTwoWeeksChecked = true;
    }

    setLastMonth(): void {
        const lastMonth = dayjs().subtract(1, 'month').toDate();

        this.setDateTo(this.today as Date);
        this.setDateFrom(lastMonth);
        this.lastMonthRange = [lastMonth.getTime(), this.today?.getTime() || 0];
        this.lastMonthChecked = true;
    }

    setSamePeriodLastYear(): void {
        // Check if not already selected
        if (!this.samePeriodLastYearChecked) {
            const toDateLastYear = dayjs(this.dateTo)
                .subtract(1, 'year')
                .toDate();
            const fromDateLastYear = dayjs(this.dateFrom)
                .subtract(1, 'year')
                .toDate();

            this.setDateTo(toDateLastYear);
            this.setDateFrom(fromDateLastYear);

            this.samePeriodLastYearRange = [
                fromDateLastYear.getTime(),
                toDateLastYear.getTime(),
            ];
            this.samePeriodLastYearChecked = true;
        }
    }

    getInputText(date: Date): string {
        return dayjs(date).format('DD-MM-YYYY');
    }

    private initializeMessage(): void {
        this.applicationService.message = new Message(
            this.message,
            'info',
            true,
            false,
        );
    }

    private initializeDates(): void {
        const filterState = this.filterService.getFilterState();

        this.dateFrom =
            filterState.orderHistoryDateFrom ||
            FilterOrderHistoryComponent.getDateForTwoWeeksAgo();
        this.dateTo = filterState.orderHistoryDateTo || new Date();
        this.today = new Date();
    }

    private normalizeDate(date: Date | null): void {
        if (date) {
            date.setHours(0, 0, 0, 0);
        }
    }

    private setPeriod(notifyFilterStateChange: boolean): void {
        this.resetOrderDateFilters();
        const filterState = this.filterService.getFilterState();

        if (
            this.dateFrom !== filterState.orderHistoryDateFrom ||
            this.dateTo !== filterState.orderHistoryDateTo
        ) {
            this.filterService.setOrderHistoryPeriod(
                notifyFilterStateChange,
                this.dateFrom as Date,
                this.dateTo as Date,
            );
        }
    }

    private checkSelectedRange(): void {
        if (this.dateFrom && this.dateTo) {
            const selectedRange: number[] = [
                this.dateFrom.getTime(),
                this.dateTo.getTime(),
            ];
            const ranges: RangeFilter[] = [
                { range: this.lastTwoWeeksRange, filter: 'lastTwoWeeks' },
                { range: this.lastMonthRange, filter: 'lastMonth' },
                {
                    range: this.samePeriodLastYearRange,
                    filter: 'samePeriodLastYear',
                },
            ];

            for (const { range, filter } of ranges) {
                if (this.compareRanges(selectedRange, range)) {
                    this.setOrderDateFilters(filter);
                    return;
                }
            }
        }

        this.resetOrderDateFilters();
    }

    private compareRanges(
        selectedRange: number[],
        predefinedRange: number[],
    ): boolean {
        if (selectedRange.length !== predefinedRange.length) {
            return false;
        }
        for (let i = 0; i < selectedRange.length; i++) {
            if (selectedRange[i] !== predefinedRange[i]) {
                return false;
            }
        }
        return true;
    }

    setOrderDateFilters(selection: string): void {
        this.resetOrderDateFilters();
        switch (selection) {
            case 'lastTwoWeeks':
                this.lastTwoWeeksChecked = true;
                break;
            case 'lastMonth':
                this.lastMonthChecked = true;
                break;
            case 'samePeriodLastYear':
                this.samePeriodLastYearChecked = true;
                break;
        }
    }

    private resetOrderDateFilters(): void {
        this.lastTwoWeeksChecked = false;
        this.lastMonthChecked = false;
        this.samePeriodLastYearChecked = false;
    }
}
